import "./ArtifactMention.css";
import { SlideShow } from "@ui/molecules/SlideShow/SlideShow.tsx";
import { Pill } from "@ui/Pill.tsx";
import { VStack } from "@ui/Stack.tsx";
import { createMemo, Match, Show, Switch } from "solid-js";
import {
	type ArtifactContent,
	artifactDownloadOutputs,
	type CSVAssetPreview,
	documentPreview,
	type VideoEvidence,
	type VideoSource,
} from "../ChatEventTypes.ts";
import { getArtifactToolbar } from "../chat-panel/cardMetadata.ts";
import { ChatCardToolbar } from "../chat-panel/components/ChatCardToolbar.tsx";
import { AssetPreviewCSV } from "./AssetPreviewCSV.tsx";
import {
	ChatBoardArtifact,
	useBoardDetailsHref,
} from "./ChatBoardArtifact.tsx";
import { ChatVideoEvidence } from "./ChatVideoEvidence.tsx";
import { DocumentView } from "./DocumentView.tsx";
import type { DocumentPreviewToolbar } from "./documentPanelEvents.ts";

function toVideoEvidence(source: VideoSource): VideoEvidence {
	const startTime = source.start_time ?? 0;
	const endTime = source.end_time ?? 0;
	return {
		video_url: source.video_url ?? "",
		thumbnail_url: source.thumbnail_url,
		aspect_ratio: source.aspect_ratio,
		start_time: startTime,
		end_time: endTime,
		video_duration: source.video_duration ?? 0,
		clip_duration: source.clip_duration ?? endTime - startTime,
		source_start_time: source.source_start_time ?? startTime,
		description: source.content_summary ?? source.title ?? "",
		title: source.content_summary ?? source.title,
		transcript: source.transcript,
		video_id: source.video_id ?? "",
		// ArtifactCreatedStreamEventProcessor sets video_id to the video's own
		// public id and video_url to the whole video, with source-absolute
		// start/end times -- exactly what source_video_id requires.
		source_video_id: source.video_id,
		hls_ready: source.hls_ready,
		influencer: source.influencer ?? {},
		social_account: source.social_account ?? undefined,
		post_url: source.post_url ?? "",
		view_count: source.view_count,
	};
}

type Props = {
	artifact: ArtifactContent;
	appEmbedded: boolean;
	interactionId: string;
	questionId: string;
	projectId?: string;
};

/**
 * Renders a mentioned artifact inline in a speaking block. The renderer is
 * selected by `artifact.type`; unknown types and artifacts with no usable data
 * fall back to a pill showing the raw type string.
 *
 * `video_sources` reuses the `video_evidence` carousel (`ChatVideoEvidence`).
 * `deck` passes hydrated slides straight to `SlideShow`. `csv` passes the
 * hydrated artifact to `AssetPreviewCSV`. `document` passes the normalised
 * artifact to `DocumentView`, whose pages open in the slide-out pane. `board`
 * shows the clips added in a `BoardCard`, like the boards list. Each
 * renderable mention includes a toolbar (type chip, optional
 * download/save/ratings).
 */
export const ArtifactMention = (props: Props) => {
	const videoEvidence = (): VideoEvidence[] =>
		(props.artifact.artifact?.sources ?? []).map(toVideoEvidence);

	const deckSlides = () => props.artifact.artifact?.slides ?? [];

	const csvPreview = (): CSVAssetPreview | undefined => {
		const artifact = props.artifact.artifact;
		if (!artifact?.headers || !artifact.rows_preview?.length) {
			return undefined;
		}
		return artifact as CSVAssetPreview;
	};

	const documentContent = createMemo(() =>
		documentPreview(props.artifact.artifact),
	);

	/**
	 * A document's title always falls back to a generic one, so it needs pages, a
	 * summary, or something to download before the card is worth rendering.
	 */
	const hasDocumentContent = () =>
		documentContent().pages.length > 0 ||
		documentContent().summary !== undefined ||
		artifactDownloadOutputs(props.artifact.artifact).length > 0;

	const boardClipsAdded = () => props.artifact.artifact?.clips_added ?? [];

	const boardId = () => {
		const id = props.artifact.artifact?.id;
		return typeof id === "string" ? id : undefined;
	};

	const boardHref = useBoardDetailsHref(boardId, () => props.projectId);

	const renderableType = ():
		| "video_sources"
		| "deck"
		| "csv"
		| "document"
		| "board"
		| undefined => {
		if (props.artifact.type === "video_sources" && videoEvidence().length > 0) {
			return "video_sources";
		}
		if (props.artifact.type === "deck" && deckSlides().length > 0) {
			return "deck";
		}
		if (props.artifact.type === "csv" && csvPreview()) {
			return "csv";
		}
		if (props.artifact.type === "document" && hasDocumentContent()) {
			return "document";
		}
		if (props.artifact.type === "board" && boardClipsAdded().length > 0) {
			return "board";
		}
		return undefined;
	};

	const viewLink = () => {
		const href = boardHref();
		return renderableType() === "board" &&
			href &&
			!props.artifact.artifact?.deleted
			? { label: "view board", href }
			: undefined;
	};

	const openVideoPanel = (
		isOpen: boolean,
		videoIndex: number,
		clips: VideoEvidence[],
	) => {
		document.dispatchEvent(
			new CustomEvent("chatevent", {
				detail: {
					type: "video_evidence",
					content: {
						is_open: isOpen,
						video_index: videoIndex,
						video_evidence: clips,
					},
				},
			}),
		);
	};

	return (
		<Show
			when={renderableType()}
			fallback={
				<Pill class="artifact-mention-pill" theme="grey200" size="small">
					{props.artifact.type}
				</Pill>
			}
		>
			{(type) => {
				const toolbar = () => getArtifactToolbar(type());
				/*
				 * Described once, because the pane a document opens in repeats the
				 * card's toolbar beneath it: the same artifact, rated and saved from
				 * either place.
				 */
				const toolbarProps = (): DocumentPreviewToolbar => ({
					questionId: props.questionId,
					interactionId: props.interactionId,
					ratingSubject: toolbar().ratingSubject,
					cardType: toolbar().cardType,
					cardTypeSymbol: toolbar().symbol,
					cardTypeIconColour: toolbar().iconColour,
					artifactId: props.artifact.id,
					saveable: toolbar().saveable,
					documentId: props.artifact.document_id,
					isFavourite: props.artifact.is_favourite,
					outputs: artifactDownloadOutputs(props.artifact.artifact),
				});
				const docAnchorId = () =>
					props.artifact.document_id
						? `doc-${props.artifact.document_id}`
						: undefined;
				return (
					<VStack
						id={docAnchorId()}
						class={`artifact-mention artifact-mention-${type().replaceAll("_", "-")}`}
						spacing="medium"
					>
						<Switch>
							<Match when={type() === "video_sources"}>
								<ChatVideoEvidence
									appEmbedded={props.appEmbedded}
									interactionId={props.interactionId}
									videoEvidence={videoEvidence()}
									projectId={props.projectId}
									setIsVideoPanelOpen={openVideoPanel}
								/>
							</Match>
							<Match when={type() === "deck"}>
								<SlideShow
									slides={deckSlides()}
									title={props.artifact.artifact?.title}
								/>
							</Match>
							<Match when={type() === "csv" && csvPreview()}>
								{(asset) => <AssetPreviewCSV asset={asset()} />}
							</Match>
							<Match when={type() === "document"}>
								<DocumentView
									document={documentContent()}
									appEmbedded={props.appEmbedded}
									toolbar={toolbarProps()}
								/>
							</Match>
							<Match when={type() === "board"}>
								<ChatBoardArtifact
									boardId={boardId()}
									title={props.artifact.artifact?.title}
									clipsAdded={boardClipsAdded()}
									privacy={props.artifact.artifact?.privacy}
									updatedAt={props.artifact.artifact?.updated_at}
									clipCount={props.artifact.artifact?.clip_count}
									deleted={props.artifact.artifact?.deleted}
								/>
							</Match>
						</Switch>
						<ChatCardToolbar {...toolbarProps()} viewLink={viewLink()} />
					</VStack>
				);
			}}
		</Show>
	);
};
